import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Building2,
  LayoutDashboard,
  Building,
  Users,
  MapPin,
  Target,
  FileUp,
  LogOut,
  Briefcase,
  Search,
  ClipboardList,
  Settings,
  Crown,
  Trophy,
  HelpCircle,
  FileText,
  Clock,
  Database,
  Ticket,
  ArrowLeftRight,
  BookOpen,
  Zap,
  CalendarDays,
  Mail,
  Package,
  HardHat,
  UserCog,
  ClipboardCheck,
  Sparkles,
  ListChecks,
  Key
} from 'lucide-react';

const Sidebar = ({ onOpenGuide }) => {
  const { user, logout, canSwitchPortal, switchPortal, activePortal } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchPortal = () => {
    switchPortal();
  };

  const isSuperAdmin = () => user?.role === 'super_admin';
  const isAdmin = () => user?.role === 'admin' || activePortal === 'admin';
  const isAgent = () => user?.role === 'agent' || activePortal === 'agent';

  const superAdminLinks = [
    { to: '/super-admin', icon: LayoutDashboard, label: 'Gestion SaaS', exact: true },
    { to: '/super-admin/companies', icon: Building, label: 'Entreprises' },
    { to: '/super-admin/roles', icon: UserCog, label: 'Gestion des Rôles' },
    { to: '/super-admin/subscription-products', icon: Package, label: 'Abonnements' },
    { to: '/super-admin/import-rnic', icon: Database, label: 'Import RNIC' },
    { to: '/super-admin/settings', icon: Settings, label: 'Paramètres' },
  ];

  const adminLinks = [
    { to: '/admin', icon: LayoutDashboard, label: 'Tableau de bord', exact: true },
    { to: '/admin/opportunites', icon: Target, label: 'Opportunités' },
    { to: '/admin/agenda', icon: CalendarDays, label: 'Agenda' },
    { to: '/admin/agents', icon: Users, label: 'Agents' },
    { to: '/admin/registre', icon: Search, label: 'Recherche Copropriétés' },
    { to: '/admin/base-nationale', icon: Database, label: 'Base Nationale' },
    { to: '/travaux', icon: HardHat, label: 'Travaux', highlight: true },
    { to: '/admin/emails', icon: Mail, label: 'Modèles Emails' },
    { to: '/admin/cee-settings', icon: Settings, label: 'Paramètres CEE' },
    { to: '/admin/formulaires', icon: ListChecks, label: 'Formulaires' },
    { to: '/admin/modeles-documents', icon: Sparkles, label: 'Documents IA' },
    { to: '/admin/personnalisation-documents', icon: FileText, label: 'Format Documents' },
    { to: '/admin/ia-config', icon: Key, label: 'Clés API IA' },
    { to: '/admin/prospect-rules', icon: Clock, label: 'Règles Prospects' },
    { to: '/admin/gamification', icon: Trophy, label: 'Gamification' },
    { to: '/admin/support', icon: HelpCircle, label: 'Support' },
    { to: '/admin/mon-entreprise', icon: Building, label: 'Mon Entreprise' },
  ];

  const agentLinks = [
    { to: '/agent', icon: LayoutDashboard, label: 'Tableau de bord', exact: true },
    { to: '/agent/recherche', icon: Search, label: 'Recherche Copropriétés' },
    { to: '/agent/prospection', icon: ClipboardList, label: 'Ma Prospection' },
    { to: '/agent/agenda', icon: CalendarDays, label: 'Agenda' },
    // Note: Travaux n'est pas accessible aux agents - réservé aux admins
  ];

  const links = isSuperAdmin() ? superAdminLinks : (isAdmin() && !isAgent() ? adminLinks : agentLinks);
  const roleLabel = isSuperAdmin() ? 'Super Admin' : (isAdmin() && !isAgent() ? 'Administration' : 'Agent Commercial');
  
  // Determine if currently viewing agent portal as admin
  const isAdminViewingAgent = user?.role === 'admin' && activePortal === 'agent';

  return (
    <div className="sidebar flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-slate-700 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${isSuperAdmin() ? 'bg-amber-500' : 'bg-[#1E3A8A]'} rounded-lg flex items-center justify-center`}>
            {isSuperAdmin() ? <Crown className="w-6 h-6 text-white" /> : <Building2 className="w-6 h-6 text-white" />}
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">CoproEnergy</h1>
            <p className={`text-xs ${isSuperAdmin() ? 'text-amber-400' : 'text-slate-400'}`}>
              {roleLabel}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation - scrollable without visible scrollbar */}
      <nav className="flex-1 py-4 overflow-y-auto min-h-0 scrollbar-hide">
        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.exact}
            data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? 'active' : ''} ${link.highlight ? 'text-amber-400 hover:text-amber-300' : ''}`
            }
          >
            <link.icon className={`w-5 h-5 ${link.highlight ? 'text-amber-500' : ''}`} strokeWidth={1.5} />
            <span>{link.label}</span>
            {link.highlight && (
              <span className="ml-auto px-1.5 py-0.5 text-[10px] font-medium bg-amber-500/20 text-amber-400 rounded">
                PRO
              </span>
            )}
          </NavLink>
        ))}
        
        {/* Guide de configuration - Only for admins */}
        {isAdmin() && !isAgent() && onOpenGuide && (
          <button
            onClick={onOpenGuide}
            data-testid="nav-guide-configuration"
            className="sidebar-link w-full text-left mt-2 hover:bg-blue-500/10 hover:text-blue-400"
          >
            <BookOpen className="w-5 h-5" strokeWidth={1.5} />
            <span>Guide de configuration</span>
          </button>
        )}
      </nav>

      {/* User section - fixed at bottom */}
      <div className="p-4 border-t border-slate-700 flex-shrink-0 bg-[#0F172A]">
        <div className="flex items-center gap-3 mb-3 px-3">
          <div className={`w-9 h-9 ${isSuperAdmin() ? 'bg-amber-500' : isAdminViewingAgent ? 'bg-emerald-600' : 'bg-slate-600'} rounded-full flex items-center justify-center text-white font-medium flex-shrink-0`}>
            {user?.prenom?.[0]}{user?.nom?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {user?.prenom} {user?.nom}
            </p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            {isAdminViewingAgent && (
              <p className="text-xs text-emerald-400 truncate">Mode démo Agent</p>
            )}
          </div>
        </div>
        
        {/* Switch Portal Button - for dual-role users */}
        {canSwitchPortal() && (
          <button
            onClick={handleSwitchPortal}
            data-testid="switch-portal-button"
            className="sidebar-link w-full text-left mb-2 hover:bg-blue-500/10 hover:text-blue-400"
          >
            <ArrowLeftRight className="w-5 h-5" strokeWidth={1.5} />
            <span>Changer de portail</span>
          </button>
        )}
        
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="sidebar-link w-full text-left text-slate-300 hover:bg-red-500/10 hover:text-red-400"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          <span>Déconnexion</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
